import type { InsightBlock, InsightSeo, InsightTheme } from '../insights-v2/types';
import type {
  PublicationBuilderBlock,
  PublicationBuilderDraft,
  PublicationRecord,
  PublicationStatus,
  UpsertPublicationPayload,
} from './types';

export const DEFAULT_PUBLICATION_THEME: InsightTheme = {
  accent: '#e0a15e',
  surface: 'linear-gradient(145deg, rgba(13, 20, 33, 0.98), rgba(27, 38, 58, 0.94))',
  glow: 'rgba(224, 161, 94, 0.2)',
  paper: '#f3f0eb',
  ink: '#142236',
  mutedInk: 'rgba(20, 34, 54, 0.72)',
};

export const DEFAULT_PUBLICATION_SEO: InsightSeo = {
  title: '',
  description: '',
  imageUrl: '',
};

function blockId(prefix: string) {
  const source =
    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  return `${prefix}-${source}`;
}

function cleanText(value: string | undefined | null) {
  return typeof value === 'string' ? value.trim() : '';
}

export function slugifyPublicationTitle(value: string) {
  return value
    .trim()
    .toLowerCase()
    .replace(/['’]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 180);
}

export function createPublicationBlock(type: PublicationBuilderBlock['type']): PublicationBuilderBlock {
  switch (type) {
    case 'heading':
      return {
        id: blockId(type),
        type: 'heading',
        text: 'New section heading',
        level: 2,
      };
    case 'paragraph':
      return {
        id: blockId(type),
        type: 'paragraph',
        text: '',
      };
    case 'image':
      return {
        id: blockId(type),
        type: 'image',
        imageUrl: '',
        alt: '',
        caption: '',
        width: 'wide',
        fit: 'cover',
      };
    case 'callout':
      return {
        id: blockId(type),
        type: 'callout',
        title: '',
        text: '',
      };
    case 'internalCta':
      return {
        id: blockId(type),
        type: 'internalCta',
        eyebrow: '',
        title: '',
        text: '',
        label: 'Read more',
        to: '/',
      };
    case 'pullQuote':
      return {
        id: blockId(type),
        type: 'pullQuote',
        quote: '',
        attribution: '',
      };
    case 'divider':
      return {
        id: blockId(type),
        type: 'divider',
      };
    case 'list':
      return {
        id: blockId(type),
        type: 'list',
        title: '',
        style: 'unordered',
        items: [''],
      };
    default:
      return {
        id: blockId('paragraph'),
        type: 'paragraph',
        text: '',
      };
  }
}

function normalizeBuilderBlocks(blocks: InsightBlock[] | undefined): PublicationBuilderBlock[] {
  if (!Array.isArray(blocks) || !blocks.length) {
    return [createPublicationBlock('paragraph')];
  }

  return blocks.map((block) => ({
    ...block,
    id: blockId(block.type),
  })) as PublicationBuilderBlock[];
}

function ensureTheme(theme?: InsightTheme | null): InsightTheme {
  return {
    ...DEFAULT_PUBLICATION_THEME,
    ...(theme ?? {}),
  };
}

function ensureSeo(seo?: InsightSeo | null): InsightSeo {
  return {
    ...DEFAULT_PUBLICATION_SEO,
    ...(seo ?? {}),
  };
}

export function createPublicationDraft(status: PublicationStatus = 'draft'): PublicationBuilderDraft {
  return {
    status,
    category: '',
    subcategory: '',
    title: '',
    subtitle: '',
    excerpt: '',
    keyPoints: [],
    publishedAt: null,
    readTime: '',
    authorFirebaseUid: '',
    authorName: '',
    authorAvatarUrl: '',
    coverImageUrl: '',
    coverImageAlt: '',
    theme: { ...DEFAULT_PUBLICATION_THEME },
    seo: { ...DEFAULT_PUBLICATION_SEO },
    blocks: [createPublicationBlock('paragraph')],
    advertisingEnabled: false,
    slug: '',
  };
}

export function publicationToDraft(publication: PublicationRecord): PublicationBuilderDraft {
  return {
    id: publication.id,
    slug: publication.slug,
    status: publication.status,
    category: publication.category,
    subcategory: publication.subcategory ?? '',
    title: publication.title,
    subtitle: publication.subtitle,
    excerpt: publication.excerpt,
    keyPoints: publication.keyPoints,
    publishedAt: publication.publishedAt,
    readTime: publication.readTime,
    authorFirebaseUid: publication.authorFirebaseUid ?? '',
    authorName: publication.author,
    authorAvatarUrl: publication.authorAvatarUrl ?? '',
    coverImageUrl: publication.coverImageUrl ?? '',
    coverImageAlt: publication.coverImageAlt ?? '',
    theme: ensureTheme(publication.theme),
    seo: ensureSeo(publication.seo),
    blocks: normalizeBuilderBlocks(publication.blocks),
    advertisingEnabled: publication.advertisingEnabled,
    createdByName: publication.createdByName,
    updatedByName: publication.updatedByName,
    createdAt: publication.createdAt,
    updatedAt: publication.updatedAt,
  };
}

export function draftToPayload(draft: PublicationBuilderDraft): UpsertPublicationPayload {
  return {
    slug: cleanText(draft.slug) || undefined,
    status: draft.status,
    category: cleanText(draft.category),
    subcategory: cleanText(draft.subcategory) || undefined,
    title: cleanText(draft.title),
    subtitle: cleanText(draft.subtitle),
    excerpt: cleanText(draft.excerpt),
    keyPoints: draft.keyPoints.map((point) => cleanText(point)).filter(Boolean),
    publishedAt: cleanText(draft.publishedAt ?? '') || null,
    readTime: cleanText(draft.readTime),
    authorFirebaseUid: cleanText(draft.authorFirebaseUid) || undefined,
    coverImageUrl: cleanText(draft.coverImageUrl) || undefined,
    coverImageAlt: cleanText(draft.coverImageAlt) || undefined,
    theme: ensureTheme(draft.theme),
    seo: {
      title: cleanText(draft.seo?.title) || undefined,
      description: cleanText(draft.seo?.description) || undefined,
      imageUrl: cleanText(draft.seo?.imageUrl) || undefined,
    },
    blocks: draft.blocks.map<InsightBlock>((block) => {
      const { id: _id, ...next } = block;
      if (block.type === 'list') {
        return {
          ...next,
          items: block.items.map((item) => cleanText(item)).filter(Boolean),
        };
      }
      return next as InsightBlock;
    }),
    advertisingEnabled: draft.advertisingEnabled,
  };
}

export function parseMultilineList(value: string) {
  return Array.from(
    new Set(
      value
        .split(/\r?\n/)
        .map((entry) => cleanText(entry))
        .filter(Boolean),
    ),
  );
}

export function formatMultilineList(values: string[]) {
  return values.join('\n');
}
